import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { LogOut, Users, Download, FileText, Home, ChevronRight, AlertCircle } from 'lucide-react';
import { StatCardSkeleton, TableRowSkeleton } from './Skeleton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);

  useEffect(() => {
    fetchCases();
  }, [filter]);

  const fetchCases = async () => {
    if (!initialLoad) setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const url = filter === 'all' ? `${API}/cases` : `${API}/cases?status=${filter}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCases(response.data);
    } catch (error) {
      console.error('Error fetching cases:', error);
      toast.error('Failed to load cases');
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    window.location.href = '/login';
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/export/servware`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const blob = new Blob([response.data.csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `servware_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${response.data.count} records`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Export failed');
    }
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'New': 'badge-new',
      'First Review': 'badge-review',
      'Second Review': 'badge-review',
      'Decision': 'badge-review',
      'Assisted': 'badge-assisted',
      'Referred': 'badge-referred',
      'Denied': 'badge-denied',
      'Closed': 'badge-closed'
    };
    return statusMap[status] || 'badge';
  };

  const getBoundaryBadge = (boundary) => {
    if (boundary === 'Inside') {
      return 'bg-emerald-400/15 text-emerald-400 border border-emerald-400/30';
    } else if (boundary === 'Outside') {
      return 'bg-amber-400/15 text-amber-400 border border-amber-400/30';
    }
    return 'bg-slate-400/15 text-slate-400 border border-slate-400/30';
  };

  const unassignedCases = cases.filter(c => !c.assigned_to && c.status === 'New');
  const myCases = cases.filter(c => c.assigned_to === user?.id);
  const allCasesCount = cases.length;

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #134e4a 100%)' }}>
      {/* Header */}
      <header className="backdrop-blur-md bg-slate-900/50 border-b border-white/10 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4 animate-fade-in">
              <div className="bg-white/10 backdrop-blur-md rounded-xl p-2 border border-white/10 hover-lift">
                <img 
                  src="/images/svdp-logo.jpg" 
                  alt="SVdP" 
                  className="h-10 w-auto"
                />
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Case Management
                </h1>
                <p className="text-sm text-slate-400">SVdP Mary, Mother of God</p>
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-3 animate-fade-in delay-100">
              <div className="text-right mr-4 hidden md:block">
                <p className="text-sm font-medium text-white">{user?.full_name}</p>
                <p className="text-xs text-slate-400">{user?.role}</p>
              </div>
              
              <button
                onClick={() => navigate('/')}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white border border-white/10 rounded-full text-sm font-medium transition-all btn-press"
                data-testid="home-btn"
              >
                <Home className="w-4 h-4" />
                <span className="hidden sm:inline">Home</span>
              </button>
              
              {user?.role === 'Admin' && (
                <button
                  onClick={() => navigate('/users')}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white border border-white/10 rounded-full text-sm font-medium transition-all btn-press"
                  data-testid="users-btn"
                >
                  <Users className="w-4 h-4" />
                  <span className="hidden sm:inline">Users</span>
                </button>
              )}
              
              {(user?.role === 'Admin' || user?.role === 'Case Lead') && (
                <button
                  onClick={handleExport}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white border border-white/10 rounded-full text-sm font-medium transition-all btn-press"
                  data-testid="export-btn"
                >
                  <Download className="w-4 h-4" />
                  <span className="hidden sm:inline">Export</span>
                </button>
              )}
              
              <button
                onClick={handleLogout}
                className="inline-flex items-center gap-2 px-4 py-2 bg-rose-500/20 hover:bg-rose-500/30 text-rose-400 border border-rose-500/30 rounded-full text-sm font-medium transition-all btn-press"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Stats Grid */}
        {initialLoad ? (
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="glass-panel p-6 animate-slide-up hover-lift card-hover-border" data-testid="stat-unassigned">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400 mb-1">Unassigned</p>
                  <p className="text-4xl font-bold text-[#0072BC]">{unassignedCases.length}</p>
                  <p className="text-xs text-slate-500 mt-1">Needs attention</p>
                </div>
                <div className="w-14 h-14 bg-blue-500/20 rounded-2xl flex items-center justify-center border border-blue-500/30 animate-float">
                  <AlertCircle className="w-7 h-7 text-blue-400" />
                </div>
              </div>
            </div>

            <div className="glass-panel p-6 animate-slide-up delay-100 hover-lift card-hover-border" data-testid="stat-my-cases">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400 mb-1">My Cases</p>
                  <p className="text-4xl font-bold text-emerald-400">{myCases.length}</p>
                  <p className="text-xs text-slate-500 mt-1">Assigned to you</p>
                </div>
                <div className="w-14 h-14 bg-emerald-500/20 rounded-2xl flex items-center justify-center border border-emerald-500/30 animate-float delay-200">
                  <FileText className="w-7 h-7 text-emerald-400" />
                </div>
              </div>
            </div>

            <div className="glass-panel p-6 animate-slide-up delay-200 hover-lift card-hover-border" data-testid="stat-all-cases">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400 mb-1">All Cases</p>
                  <p className="text-4xl font-bold text-purple-400">{allCasesCount}</p>
                  <p className="text-xs text-slate-500 mt-1">Total in system</p>
                </div>
                <div className="w-14 h-14 bg-purple-500/20 rounded-2xl flex items-center justify-center border border-purple-500/30 animate-float delay-400">
                  <FileText className="w-7 h-7 text-purple-400" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="glass-panel p-4 mb-6 animate-slide-up delay-300">
          <div className="flex flex-wrap gap-2">
            {['all', 'New', 'First Review', 'Second Review', 'Assisted', 'Referred', 'Denied', 'Closed'].map((status, index) => (
              <button
                key={status}
                onClick={() => setFilter(status === 'all' ? 'all' : status)}
                className={`px-4 py-2 rounded-full font-medium text-sm transition-all btn-press ${
                  filter === status || (filter === 'all' && status === 'all')
                    ? 'bg-[#0072BC] text-white shadow-lg shadow-blue-900/30 animate-pulse-glow'
                    : 'bg-white/5 text-slate-300 hover:bg-white/10 hover:text-white border border-white/10'
                }`}
                style={{ animationDelay: `${index * 50}ms` }}
                data-testid={`filter-${status.toLowerCase().replace(' ', '-')}`}
              >
                {status === 'all' ? 'All Cases' : status}
              </button>
            ))}
          </div>
        </div>

        {/* Cases List */}
        <div className="glass-panel overflow-hidden animate-slide-up delay-400">
          {loading ? (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="cases-table-loading">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Case #</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Request Types</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Boundary</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Created</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <TableRowSkeleton key={i} />
                  ))}
                </tbody>
              </table>
            </div>
          ) : cases.length === 0 ? (
            <div className="p-12 text-center animate-scale-in" data-testid="no-cases">
              <div className="animate-float">
                <FileText className="w-16 h-16 mx-auto mb-4 text-slate-600" />
              </div>
              <p className="text-slate-400 text-lg">No cases found</p>
              <p className="text-slate-500 text-sm mt-1">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="cases-table">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Case #</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Request Types</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Boundary</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Created</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {cases.map((case_item, index) => (
                    <tr 
                      key={case_item.id} 
                      className="hover:bg-white/5 transition-colors cursor-pointer animate-fade-in"
                      style={{ animationDelay: `${index * 50}ms` }}
                      onClick={() => navigate(`/cases/${case_item.id}`)}
                      data-testid={`case-row-${case_item.id}`}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-white">{case_item.case_number}</span>
                          {case_item.previous_case_count > 0 && (() => {
                            const days = case_item.most_recent_prior_days;
                            let cls, label;
                            if (days !== null && days <= 90) {
                              cls = 'bg-rose-500/15 text-rose-300 border-rose-500/30';
                              label = 'Recent';
                            } else if (days !== null && days <= 365) {
                              cls = 'bg-amber-500/15 text-amber-300 border-amber-500/30';
                              label = 'Prior';
                            } else {
                              cls = 'bg-slate-500/15 text-slate-300 border-slate-500/30';
                              label = 'Past';
                            }
                            const tip = `${case_item.previous_case_count} previous case${case_item.previous_case_count > 1 ? 's' : ''}` + (days !== null ? ` — last one ${days} day${days === 1 ? '' : 's'} ago` : '');
                            return (
                              <span
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${cls}`}
                                title={tip}
                                data-testid={`returning-chip-${case_item.id}`}
                              >
                                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 1l4 4-4 4"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><path d="M7 23l-4-4 4-4"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>
                                {label} · {case_item.previous_case_count}
                              </span>
                            );
                          })()}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`badge ${getStatusBadgeClass(case_item.status)}`}>
                          {case_item.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-slate-300">
                          {case_item.request_types.join(', ')}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm font-medium text-white">
                          {case_item.amount_requested ? `$${case_item.amount_requested.toFixed(2)}` : '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getBoundaryBadge(case_item.boundary_check)}`}>
                          {case_item.boundary_check || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-slate-400">
                          {new Date(case_item.created_at).toLocaleDateString()}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/cases/${case_item.id}`);
                          }}
                          className="inline-flex items-center gap-1 text-[#0072BC] hover:text-[#60a5fa] font-medium text-sm transition-colors group"
                          data-testid={`view-case-${case_item.id}`}
                        >
                          View
                          <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
